/**
 * A shared node, rendered as a node.
 *
 * A node on the canvas is one question and one answer on a card — not a
 * transcript with a scrollback. Published through the chat viewer it arrived as
 * the latter: a conversation of one exchange, framed as correspondence, with
 * the tool calls that produced the answer laid out as if the reader had been
 * there. That is a true record of the run and the wrong picture of the thing.
 *
 * So a node share is drawn the way the canvas draws it: the question at the top
 * in the shape a prompt has, the answer below it, on one card of the width a
 * node has. The steps are deliberately not shown — the node does not show them
 * either, and a reader given a link to an answer was given the answer.
 *
 * A conversation is still a conversation and still goes to the chat viewer;
 * this is only for the single exchange.
 */
import { Markdown, openExternalUrl } from '@synti/ui'
import type { StoredSession } from '@synti/core'

const openUrl = (url: string) => {
  const result = openExternalUrl(url)
  if (!result.opened) console.warn('[viewer:node] blocked URL:', url)
}

/** The width a node has on the canvas, so a shared one is the same object. */
const NODE_WIDTH = 640

export function AirNodeView({ session }: { session: StoredSession }) {
  const messages = session.messages ?? []
  const prompt = messages.find((m) => m.type === 'user')?.content?.trim()
  // Every answer, in order. A run that spoke more than once said one thing in
  // instalments; joining them is what the node shows.
  const answer = messages
    .filter((m) => m.type === 'assistant' && typeof m.content === 'string' && m.content.trim())
    .map((m) => m.content.trim())
    .join('\n\n')

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8 sm:px-6">
      <div className="mx-auto w-full" style={{ maxWidth: NODE_WIDTH }}>
        <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
          {prompt && (
            // The question, in the shape it has on the card: quiet, above the
            // answer, and never competing with it for attention.
            <div className="border-b border-border/60 px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
              {prompt}
            </div>
          )}
          <div className="px-4 py-4 text-[14px] leading-relaxed [&_img]:my-2 [&_img]:block [&_img]:max-h-[280px] [&_img]:w-full [&_img]:rounded-xl [&_img]:border [&_img]:border-border [&_img]:object-cover">
            {answer ? (
              <Markdown mode="minimal" onUrlClick={openUrl}>
                {answer}
              </Markdown>
            ) : (
              // A node that ran and said nothing is a node that said nothing.
              // Better stated than left as an empty card the reader assumes is
              // still loading.
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
